export const ONLINE_DIVISIONS = [
  'PROVINCIAL',
  'AUTONOMICA',
  'NACIONAL',
  'CONTINENTAL',
  'MUNDIAL',
] as const;

export type OnlineDivision = typeof ONLINE_DIVISIONS[number];
export type OnlineMatchOutcome = 'WIN' | 'DRAW' | 'LOSS' | 'VOID';

export interface OnlineLeagueConfig {
  version: string;
  engineVersion: string;
  seasonDurationDays: number;
  initialDivision: OnlineDivision;
  initialLeaguePoints: number;
  winPoints: number;
  drawPoints: number;
  lossPoints: number;
  abandonmentPoints: number;
  promotionThresholdByDivision: Partial<Record<OnlineDivision, number>>;
  relegationThresholdByDivision: Partial<Record<OnlineDivision, number>>;
  promotedStartingPoints: number;
  relegatedStartingPoints: number;
  minimumMatchesBeforePromotion: number;
  placementMatches: number;
  ratingBase: number;
  ratingFloor: number;
  ratingKFactor: number;
  placementKFactor: number;
  maxProvisionalRankDiff: number;
  matchmakingTicketTtlSeconds: number;
  matchmakingBaseMmrTolerance: number;
  matchmakingMmrTolerancePerMinute: number;
  matchmakingAdjacentDivisionWaitSeconds: number;
  matchmakingRematchCooldownMinutes: number;
  matchmakingMaxSquadPowerDiff: number;
  workerLeaseSeconds: number;
  maxSimulationAttempts: number;
  replayFrameIntervalSeconds: number;
}

export interface LeaguePointContext {
  abandoned?: boolean;
  voided?: boolean;
}

export interface RatingResult {
  outcome: OnlineMatchOutcome;
  currentRating: number;
  matchesPlayed: number;
}

export interface OnlineRankState {
  division: OnlineDivision;
  divisionPoints: number;
  hiddenMmr: number;
  wins: number;
  draws: number;
  losses: number;
  pointsReachedAt: string;
  publicPlayerId?: string;
}

export interface RankTransition {
  state: OnlineRankState;
  change: 'PROMOTED' | 'RELEGATED' | 'UNCHANGED';
  previousDivision: OnlineDivision;
}

export const DEFAULT_ONLINE_LEAGUE_CONFIG: OnlineLeagueConfig = {
  version: 'online-v1',
  engineVersion: 'utility-ai-3.2.1',
  seasonDurationDays: 28,
  initialDivision: 'PROVINCIAL',
  initialLeaguePoints: 0,
  winPoints: 25,
  drawPoints: 5,
  lossPoints: -15,
  abandonmentPoints: -25,
  promotionThresholdByDivision: {
    PROVINCIAL: 100,
    AUTONOMICA: 100,
    NACIONAL: 100,
    CONTINENTAL: 100,
  },
  relegationThresholdByDivision: {
    AUTONOMICA: 0,
    NACIONAL: 0,
    CONTINENTAL: 0,
    MUNDIAL: 0,
  },
  promotedStartingPoints: 35,
  relegatedStartingPoints: 65,
  minimumMatchesBeforePromotion: 5,
  placementMatches: 10,
  ratingBase: 1_000,
  ratingFloor: 100,
  ratingKFactor: 24,
  placementKFactor: 40,
  maxProvisionalRankDiff: 300,
  matchmakingTicketTtlSeconds: 600,
  matchmakingBaseMmrTolerance: 60,
  matchmakingMmrTolerancePerMinute: 30,
  matchmakingAdjacentDivisionWaitSeconds: 90,
  matchmakingRematchCooldownMinutes: 30,
  matchmakingMaxSquadPowerDiff: 120,
  workerLeaseSeconds: 300,
  maxSimulationAttempts: 3,
  replayFrameIntervalSeconds: 5,
};

const DIVISION_LABELS: Record<OnlineDivision, string> = {
  PROVINCIAL: 'Provincial',
  AUTONOMICA: 'Autonómica',
  NACIONAL: 'Nacional',
  CONTINENTAL: 'Continental',
  MUNDIAL: 'Mundial',
};

export function getDivisionOrder(): readonly OnlineDivision[];
export function getDivisionOrder(division: OnlineDivision): number;
export function getDivisionOrder(division?: OnlineDivision): readonly OnlineDivision[] | number {
  return division ? ONLINE_DIVISIONS.indexOf(division) : ONLINE_DIVISIONS;
}

export function getDivisionLabel(division: OnlineDivision): string {
  return DIVISION_LABELS[division];
}

export function calculateLeaguePointDelta(
  result: OnlineMatchOutcome,
  config: OnlineLeagueConfig,
  context: LeaguePointContext = {},
): number {
  if (context.voided || result === 'VOID') return 0;
  if (context.abandoned) return config.abandonmentPoints;
  if (result === 'WIN') return config.winPoints;
  if (result === 'DRAW') return config.drawPoints;
  return config.lossPoints;
}

/**
 * Applies a conventional Elo update. The expected result is
 * `1 / (1 + 10 ^ ((opponent - player) / 400))`; placement games use the
 * larger configurable K-factor and all ratings are rounded to integers.
 */
export function calculateNewRating(
  result: RatingResult,
  opponentRating: number,
  config: OnlineLeagueConfig,
): number {
  if (result.outcome === 'VOID') return result.currentRating;
  const actual = result.outcome === 'WIN' ? 1 : result.outcome === 'DRAW' ? 0.5 : 0;
  const expected = 1 / (1 + 10 ** ((opponentRating - result.currentRating) / 400));
  const kFactor = result.matchesPlayed < config.placementMatches
    ? config.placementKFactor
    : config.ratingKFactor;
  return Math.max(config.ratingFloor, Math.round(result.currentRating + kFactor * (actual - expected)));
}

export function applyPromotionOrRelegation(
  rankState: OnlineRankState,
  config: OnlineLeagueConfig,
): RankTransition {
  const divisionIndex = ONLINE_DIVISIONS.indexOf(rankState.division);
  const matchesPlayed = rankState.wins + rankState.draws + rankState.losses;
  const promotionThreshold = config.promotionThresholdByDivision[rankState.division];
  if (
    divisionIndex < ONLINE_DIVISIONS.length - 1
    && promotionThreshold !== undefined
    && rankState.divisionPoints >= promotionThreshold
    && matchesPlayed >= config.minimumMatchesBeforePromotion
  ) {
    return {
      previousDivision: rankState.division,
      change: 'PROMOTED',
      state: {
        ...rankState,
        division: ONLINE_DIVISIONS[divisionIndex + 1],
        divisionPoints: config.promotedStartingPoints,
      },
    };
  }

  const relegationThreshold = config.relegationThresholdByDivision[rankState.division];
  if (
    divisionIndex > 0
    && relegationThreshold !== undefined
    && rankState.divisionPoints < relegationThreshold
  ) {
    return {
      previousDivision: rankState.division,
      change: 'RELEGATED',
      state: {
        ...rankState,
        division: ONLINE_DIVISIONS[divisionIndex - 1],
        divisionPoints: config.relegatedStartingPoints,
      },
    };
  }

  return {
    previousDivision: rankState.division,
    change: 'UNCHANGED',
    state: rankState.division === 'PROVINCIAL' && rankState.divisionPoints < 0
      ? { ...rankState, divisionPoints: 0 }
      : { ...rankState },
  };
}

function compareStableTies(first: OnlineRankState, second: OnlineRankState): number {
  return second.divisionPoints - first.divisionPoints
    || second.wins - first.wins
    || first.pointsReachedAt.localeCompare(second.pointsReachedAt)
    || (first.publicPlayerId ?? '').localeCompare(second.publicPlayerId ?? '');
}

export function compareDivisionRank(first: OnlineRankState, second: OnlineRankState): number {
  return compareStableTies(first, second);
}

export function compareGlobalRank(first: OnlineRankState, second: OnlineRankState): number {
  return getDivisionOrder(second.division) - getDivisionOrder(first.division)
    || compareStableTies(first, second);
}
